import React, { useEffect, useState } from "react";
import { apiFetch, getGlobalVariables } from "../utils/wkCommon";
import { toPinyinSync, capitalizePinyin } from "../utils/pinyinUtil";
import { getUser } from "../utils/auth";

const user = getUser();

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SongForm({ data, onClose, onSaved }) {

  const [form, setForm] = useState({
    JudulNada: "",
    JudulNadaPinyin: "",
    JudulLirik: "",
    JudulLirikPinyin: "",
    PrimaryLanguage: "",
    EmotionTags: [],
    KeywordTags: [],
    song_text: "",
    song_translation: "",
    notes: ""
  });

  const fieldStyle = {
    marginBottom: "0rem"
  };

  const labelTextStyle = {
    fontSize: "0.75rem",
    color: "#666",
    marginBottom: "0rem"
  };

  const [globals, setGlobals] = useState({});

  // =========================
  // STYLE
  // =========================
  const inputStyle = {
    width: "95%",
    padding: "0.625rem 0.875rem",
    borderRadius: "0.625rem",
    border: "1px solid #ddd",
    fontSize: "0.875rem"
  };

  const gridStyle = {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: "0.5rem"
  };

  const labelStyle = {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    fontSize: "0.875rem"
  };

  // =========================
  // LOAD GLOBALS
  // =========================
  useEffect(() => {
    getGlobalVariables([
      "SONG_PRIMARY_LANGUAGE",
      "SONG_EMOTION_TAGS",
      "SONG_KEYWORD_TAGS"
    ]).then(setGlobals);
  }, []);

  // =========================
  // LOAD DATA (EDIT MODE)
  // =========================
  useEffect(() => {
    if (data) {
      setForm({
        JudulNada: data.JudulNada || "",
        JudulNadaPinyin: data.JudulNadaPinyin || "",
        JudulLirik: data.JudulLirik || "",
        JudulLirikPinyin: data.JudulLirikPinyin || "",
        PrimaryLanguage: data.PrimaryLanguage || "",
        EmotionTags: (data.EmotionTags || []).map(t =>
          typeof t === "string" ? { id: t, zh: t } : t
        ),
        KeywordTags: (data.KeywordTags || []).map(t =>
          typeof t === "string" ? { id: t, zh: t } : t
        ),
        song_text: data.song_text || "",
        song_translation: data.song_translation || "",
        notes: data.notes || ""
      });
    } else {
      // 👉 RESET ketika mode ADD
      setForm({
        JudulNada: "",
        JudulNadaPinyin: "",
        JudulLirik: "",
        JudulLirikPinyin: "",
        PrimaryLanguage: "",
        EmotionTags: [],
        KeywordTags: [],
        song_text: "",
        song_translation: "",
        notes: ""
      });
    }
  }, [data]);

  // =========================
  // AUTO PINYIN
  // =========================
  useEffect(() => {
    if (form.JudulNada && !data?.JudulNadaPinyin) {
      setForm(f => ({
        ...f,
        JudulNadaPinyin: capitalizePinyin(toPinyinSync(f.JudulNada))
      }));
    }
  }, [form.JudulNada]);

  useEffect(() => {
    if (form.JudulLirik && !data?.JudulLirikPinyin) {
      setForm(f => ({
        ...f,
        JudulLirikPinyin: capitalizePinyin(toPinyinSync(f.JudulLirik))
      }));
    }
  }, [form.JudulLirik]);

  // =========================
  // SAVE
  // =========================
  async function handleSave() {
    const base = { ...form };

    const body = {
      data: data?.documentId
        ? {
            ...base,
            recordEditor: user?.username
          }
        : {
            ...base,
            recordOwner: user?.username,
            recordEditor: user?.username
          }
    };
    
    if (data?.documentId) {
      await apiFetch(`/api/virtuous-songs/${data.documentId}`, {
        method: "PUT",
        body
      });
    } else {
      await apiFetch(`/api/virtuous-songs`, {
        method: "POST",
        body
      });
    }

    onSaved();
    onClose();
  }

  // =========================
  // RENDER
  // =========================
  return (
    <>
      {/* INPUT */}
      <div style={fieldStyle}>
        <div style={labelTextStyle}>調寄 Judul Nada</div>
        <input
          style={inputStyle}
          value={form.JudulNada}
          onChange={e => setForm({ ...form, JudulNada: e.target.value })}
        />
      </div>

      <div style={fieldStyle}>
        <div style={labelTextStyle}>調寄拼音 Judul Nada (Pinyin)</div>
        <input
          style={inputStyle}
          value={form.JudulNadaPinyin}
          onChange={e => setForm({ ...form, JudulNadaPinyin: e.target.value })}
        />
      </div>

      <div style={fieldStyle}>
        <div style={labelTextStyle}>語寄 Judul Lirik</div>
        <input
          style={inputStyle}
          value={form.JudulLirik}
          onChange={e => setForm({ ...form, JudulLirik: e.target.value })}
        />
      </div>

      <div style={fieldStyle}>
        <div style={labelTextStyle}>語寄拼音 Judul Lirik (Pinyin)</div>
        <input
          style={inputStyle}
          value={form.JudulLirikPinyin}
          onChange={e => setForm({ ...form, JudulLirikPinyin: e.target.value })}
        />
      </div>

      {/* PRIMARY LANGUAGE */}
      <div style={fieldStyle}>
        <div style={labelTextStyle}>主語 Primary Language</div>
        <select
          style={inputStyle}
          value={form.PrimaryLanguage}
          onChange={e => {
            const selected = (globals.SONG_PRIMARY_LANGUAGE || [])
              .find(x => (x.zh + " " + x.id) === e.target.value);

            setForm({
              ...form,
              PrimaryLanguage: selected
                ? `${selected.zh} ${selected.id}`
                : ""
            });
          }}
        >
          <option value="">-- Select --</option>
          {(globals.SONG_PRIMARY_LANGUAGE || []).map(i => {
            const value = `${i.zh} ${i.id}`;
            return (
              <option key={i.id} value={value}>
                {i.zh} {i.id}
              </option>
            );
          })}
        </select>
      </div>

      {/* EMOTION TAGS */}
      <div>
        <div style={{ fontWeight: 600 }}>Emotion</div>
        <div style={gridStyle}>
          {(globals.SONG_EMOTION_TAGS || []).map(tag => (
            <label key={tag.id} style={labelStyle}>
              <input
                type="checkbox"
                checked={form.EmotionTags.some(x => x.id === tag.id)}
                onChange={(e) => {
                  const arr = e.target.checked
                    ? [...form.EmotionTags, { id: tag.id, zh: tag.zh }]
                    : form.EmotionTags.filter(x => x.id !== tag.id);
                  setForm({ ...form, EmotionTags: arr });
                }}
              />
              {tag.id} ({tag.zh})
            </label>
          ))}
        </div>
      </div>

      {/* KEYWORD TAGS */}
      <div>
        <div style={{ fontWeight: 600 }}>Keyword</div>
        <div style={gridStyle}>
          {(globals.SONG_KEYWORD_TAGS || []).map(tag => (
            <label key={tag.id} style={labelStyle}>
              <input
                type="checkbox"
                checked={form.KeywordTags.some(x => x.id === tag.id)}
                onChange={(e) => {
                  const arr = e.target.checked
                    ? [...form.KeywordTags, { id: tag.id, zh: tag.zh }]
                    : form.KeywordTags.filter(x => x.id !== tag.id);
                  setForm({ ...form, KeywordTags: arr });
                }}
              />
              {tag.id} ({tag.zh})
            </label>
          ))}
        </div>
      </div>

      {/* NOTES */}
      <div style={fieldStyle}>
        <div style={labelTextStyle}>備註 Notes</div>
        <textarea
          style={{ ...inputStyle, minHeight: "5rem" }}
          placeholder="Notes"
          value={form.notes}
          onChange={e => setForm({ ...form, notes: e.target.value })}
        />
      </div>

      {/* SONG TEXT */}
      <div style={fieldStyle}>
        <div style={labelTextStyle}>歌詞 Teks Lagu</div>
        <textarea
          style={{ ...inputStyle, minHeight: "6rem" }}
          placeholder="Song Text"
          value={form.song_text}
          onChange={e => setForm({ ...form, song_text: e.target.value })}
        />
      </div>

      {/* SONG TRANSLATION */}
      <div style={fieldStyle}>
        <div style={labelTextStyle}>歌詞翻譯 Arti Lagu (Translation)</div>
        <textarea
          style={{ ...inputStyle, minHeight: "6rem" }}
          placeholder="Song Translation"
          value={form.song_translation}
          onChange={e => setForm({ ...form, song_translation: e.target.value })}
        />
      </div>

      {/* SAVE */}
      <button
        className="btn-primary"
        style={{ marginTop: "0.75rem" }}
        onClick={handleSave}
      >
        Save
      </button>
    </>
  );
}